import { useQuery } from "@powersync/react";
import { useApp } from "@/app/AppContext";

// Every screen reads through these hooks. They query the local synced SQLite,
// so reads are instant and work offline; results re-render as sync lands.

export interface Team { id: string; name: string; level_hierarchy: string | null; weather_unit_group: string | null }
export interface Person { user_id: string; name: string | null; last_name: string | null; email: string | null }
export interface Location {
  id: string; label: string | null; level: string | null; description: string | null; geometry: string | null; geometry_type: string | null;
  latitude: string | null; longitude: string | null; map_id: string | null; responsible_user_id: string | null; map_feature_status_id: string | null;
  updated_at: string | null; created_at: string | null;
}
export interface Visit {
  id: string; title: string | null; user_id: string | null; map_feature_id: string | null; started_at: string | null; completed_at: string | null;
  processing_state: string | null; ai_summary: string | null; ai_processed: number | null; created_at: string | null; updated_at: string | null;
  location_label?: string | null; location_level?: string | null; obs_count?: number; photo_count?: number; audio_count?: number;
  report_id?: string | null; report_approved_at?: string | null;
}
export interface Observation {
  id: string; whatsapp_message_id: string | null; field_note_id: string | null; map_feature_id: string | null; user_id: string | null;
  content_type: string | null; storage_key: string | null; filename: string | null; body: string | null; raw_transcript: string | null;
  whatsapp_message_content_ai: string | null; image_text_ai: string | null; latitude: string | null; longitude: string | null;
  location_label: string | null; processed: number | null; created_at: string | null;
}
export interface Report {
  id: string; field_note_id: string | null; map_feature_id: string | null; status: string | null; config_name: string | null; created_at: string | null;
  generated_at: string | null; report_data: string | null; approved_at: string | null; approver_name: string | null;
  visit_title?: string | null; location_label?: string | null;
}
export interface Reminder {
  id: string; details: string | null; reminder_date: string | null; reminder_time: string | null; assigned_user_id: string | null; creator_user_id: string | null;
  map_feature_id: string | null; field_note_id: string | null; status: number | null; completed_at: string | null; completed_by_id?: string | null; canceled_at: string | null; ai_created: number | null;
  created_at: string | null; location_label?: string | null; reminder_type?: number | null; location_level?: string | null; parent_label?: string | null; visit_title?: string | null; visit_started_at?: string | null;
}
export interface Contact { id: string; name: string | null; email: string | null; phone: string | null; role: string | null; notes: string | null; created_at: string | null; locations?: string | null }
export interface Comment { id: string; content: string | null; user_id: string | null; map_feature_id: string | null; field_note_id: string | null; ai_generated: number | null; status: string | null; created_at: string | null }

const one = <T,>(rows: T[]) => rows[0] ?? null;

export function useTeams() {
  return useQuery<Team>("SELECT id, name, level_hierarchy, weather_unit_group FROM teams ORDER BY name");
}

export function usePeople() {
  const { teamId } = useApp();
  return useQuery<Person>("SELECT user_id, name, last_name, email FROM mobile_team_users WHERE team_id = ? GROUP BY user_id ORDER BY name", [teamId]);
}

// Locations are map features on the team's maps.
const LOC = "SELECT mf.* FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ?";

export function useLocations(level?: string | null) {
  const { teamId } = useApp();
  return useQuery<Location & { visit_count: number; last_visit: string | null; child_count: number; parent_label: string | null }>(
    `SELECT mf.*,
       (SELECT count(*) FROM mobile_field_notes fn WHERE fn.map_feature_id = mf.id
          OR fn.map_feature_id IN (SELECT member_id FROM mobile_zone_memberships WHERE zone_id = mf.id)) AS visit_count,
       (SELECT max(coalesce(fn.completed_at, fn.started_at)) FROM mobile_field_notes fn WHERE fn.map_feature_id = mf.id
          OR fn.map_feature_id IN (SELECT member_id FROM mobile_zone_memberships WHERE zone_id = mf.id)) AS last_visit,
       (SELECT count(*) FROM mobile_zone_memberships z WHERE z.zone_id = mf.id) AS child_count,
       (SELECT p.label FROM mobile_zone_memberships z JOIN map_features p ON p.id = z.zone_id WHERE z.member_id = mf.id LIMIT 1) AS parent_label
     FROM (${LOC}) mf ${level ? "WHERE mf.level = ?" : ""} ORDER BY mf.label COLLATE NOCASE`,
    level ? [teamId, level] : [teamId],
  );
}

export function useLocation(id: string) {
  const q = useQuery<Location>("SELECT * FROM map_features WHERE id = ?", [id]);
  return { ...q, data: one(q.data) };
}

export function useChildren(id: string) {
  return useQuery<Location>(
    "SELECT mf.* FROM mobile_zone_memberships z JOIN map_features mf ON mf.id = z.member_id WHERE z.zone_id = ? ORDER BY mf.label COLLATE NOCASE",
    [id],
  );
}

export function useAncestors(id: string) {
  return useQuery<Location>(
    `WITH RECURSIVE up(id, depth) AS (
       SELECT zone_id, 1 FROM mobile_zone_memberships WHERE member_id = ?
       UNION SELECT z.zone_id, up.depth + 1 FROM mobile_zone_memberships z JOIN up ON z.member_id = up.id WHERE up.depth < 6)
     SELECT mf.*, up.depth FROM up JOIN map_features mf ON mf.id = up.id ORDER BY up.depth DESC`,
    [id],
  );
}

const VISIT_SELECT = `SELECT fn.*, mf.label AS location_label, mf.level AS location_level,
    (SELECT count(*) FROM mobile_field_note_media o WHERE o.field_note_id = fn.id) AS obs_count,
    (SELECT count(*) FROM mobile_field_note_media o WHERE o.field_note_id = fn.id AND o.content_type LIKE 'image/%') AS photo_count,
    (SELECT count(*) FROM mobile_field_note_media o WHERE o.field_note_id = fn.id AND o.content_type LIKE 'audio/%') AS audio_count,
    (SELECT r.id FROM mobile_custom_report_statuses r WHERE r.field_note_id = fn.id ORDER BY r.created_at DESC LIMIT 1) AS report_id,
    (SELECT r.approved_at FROM mobile_custom_report_statuses r WHERE r.field_note_id = fn.id ORDER BY r.created_at DESC LIMIT 1) AS report_approved_at
  FROM mobile_field_notes fn LEFT JOIN map_features mf ON mf.id = fn.map_feature_id
  LEFT JOIN maps m ON m.id = coalesce(fn.map_id, mf.map_id)`;

export function useVisits(opts: { locationIds?: string[]; limit?: number } = {}) {
  const { teamId } = useApp();
  const ids = opts.locationIds;
  const where = ["m.team_id = ?"];
  const params: unknown[] = [teamId];
  if (ids) {
    where.push(`fn.map_feature_id IN (${ids.map(() => "?").join(",") || "NULL"})`);
    params.push(...ids);
  }
  return useQuery<Visit>(
    `${VISIT_SELECT} WHERE ${where.join(" AND ")} ORDER BY coalesce(fn.started_at, fn.created_at) DESC ${opts.limit ? `LIMIT ${Math.floor(opts.limit)}` : ""}`,
    params,
  );
}

export function useVisit(id: string) {
  const q = useQuery<Visit>(`${VISIT_SELECT} WHERE fn.id = ?`, [id]);
  return { ...q, data: one(q.data) };
}

export function useObservations(visitId: string) {
  return useQuery<Observation>("SELECT * FROM mobile_field_note_media WHERE field_note_id = ? ORDER BY created_at", [visitId]);
}

export function useReports() {
  const { teamId } = useApp();
  return useQuery<Report>(
    `SELECT r.*, fn.title AS visit_title, mf.label AS location_label FROM mobile_custom_report_statuses r
     LEFT JOIN mobile_field_notes fn ON fn.id = r.field_note_id LEFT JOIN map_features mf ON mf.id = r.map_feature_id
     JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? ORDER BY coalesce(r.generated_at, r.created_at) DESC`,
    [teamId],
  );
}

export function useVisitReport(visitId: string) {
  const q = useQuery<Report>("SELECT * FROM mobile_custom_report_statuses WHERE field_note_id = ? ORDER BY created_at DESC LIMIT 1", [visitId]);
  return { ...q, data: one(q.data) };
}

export function useReminders() {
  const { teamId } = useApp();
  return useQuery<Reminder>(
    `${REMINDER_SELECT} LEFT JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? OR r.map_feature_id IS NULL ORDER BY r.reminder_date, r.reminder_time`,
    [teamId],
  );
}

const REMINDER_SELECT = `SELECT r.*, mf.label AS location_label, mf.level AS location_level,
    (SELECT p.label FROM mobile_zone_memberships z JOIN map_features p ON p.id = z.zone_id WHERE z.member_id = mf.id LIMIT 1) AS parent_label,
    fn.title AS visit_title, fn.started_at AS visit_started_at
  FROM mobile_reminders r LEFT JOIN map_features mf ON mf.id = r.map_feature_id LEFT JOIN mobile_field_notes fn ON fn.id = r.field_note_id`;

export function useReminder(id?: string | null) {
  const q = useQuery<Reminder>(`${REMINDER_SELECT} WHERE r.id = ?`, [id ?? ""]);
  return { ...q, data: one(q.data) };
}

export function useContacts() {
  const { teamId } = useApp();
  return useQuery<Contact>(
    `SELECT c.*, (SELECT group_concat(mf.label, ' · ') FROM mobile_feature_contacts fc JOIN map_features mf ON mf.id = fc.map_feature_id WHERE fc.client_contact_id = c.id) AS locations
     FROM client_contacts c WHERE c.team_id = ? ORDER BY c.name COLLATE NOCASE`,
    [teamId],
  );
}

export function useLocationContacts(locationId: string) {
  return useQuery<Contact>(
    "SELECT c.* FROM mobile_feature_contacts fc JOIN client_contacts c ON c.id = fc.client_contact_id WHERE fc.map_feature_id = ?",
    [locationId],
  );
}

export function useComments(visitId: string) {
  return useQuery<Comment>("SELECT * FROM mobile_map_feature_comments WHERE field_note_id = ? ORDER BY created_at", [visitId]);
}

export function useNotifications() {
  return useQuery<{ id: string; title: string | null; message: string | null; read_at: string | null; created_at: string | null }>(
    "SELECT id, title, message, read_at, created_at FROM notifications ORDER BY created_at DESC LIMIT 30",
  );
}
